import { Trans } from '@lingui/macro';
import GitHub from '@mui/icons-material/GitHub';
import Instagram from '@mui/icons-material/Instagram';
import LinkedIn from '@mui/icons-material/LinkedIn';
import X from '@mui/icons-material/X';
import { Box, Container, styled, SvgIcon, Typography } from '@mui/material';
import { DuneIcon, TikTok } from 'public/icons/footer/icons';
import { Link } from 'src/components/primitives/Link';
import { useRootStore } from 'src/store/root';
import { figVars } from 'src/utils/figmaColors';
import { useShallow } from 'zustand/shallow';

import DiscordIcon from '/public/icons/discord.svg';

interface StyledLinkProps {
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

const StyledLink = styled(Link)<StyledLinkProps>({
  color: figVars['fg-3'],
  '&:hover': {
    color: figVars['fg-1'],
  },
  display: 'flex',
  alignItems: 'center',
});

const FOOTER_ICONS = [
  {
    href: 'https://x.com/aave',
    icon: <X />,
    title: 'X',
  },
  {
    href: 'https://www.instagram.com/aave/',
    icon: <Instagram />,
    title: 'Instagram',
  },
  {
    href: 'https://www.tiktok.com/@aavelabs',
    icon: <TikTok />,
    title: 'TikTok',
  },
  {
    href: 'https://www.linkedin.com/company/aavelabs/',
    icon: <LinkedIn />,
    title: 'Linkedin',
  },
  {
    href: 'https://discord.com/invite/aave',
    icon: <DiscordIcon />,
    title: 'Discord',
  },
  {
    href: 'https://dune.com/aavelabs',
    icon: <DuneIcon />,
    title: 'Dune',
  },
  {
    href: 'https://github.com/aave',
    icon: <GitHub />,
    title: 'Github',
  },
];

export function AppFooter() {
  const [setAnalyticsConfigOpen, setFeedbackOpen] = useRootStore(
    useShallow((store) => [store.setAnalyticsConfigOpen, store.setFeedbackOpen])
  );

  const FOOTER_LINKS = [
    {
      href: 'https://aave.com/terms-of-service',
      label: <Trans>Terms</Trans>,
      key: 'Terms',
    },
    {
      href: 'https://aave.com/privacy-policy/',
      label: <Trans>Privacy</Trans>,
      key: 'Privacy',
    },
    {
      href: 'https://docs.aave.com/hub/',
      label: <Trans>Docs</Trans>,
      key: 'Docs',
    },
    {
      href: 'https://docs.aave.com/faq/',
      label: <Trans>FAQS</Trans>,
      key: 'FAQS',
    },
    {
      href: 'https://discord.com/invite/aave',
      label: <Trans>Get Support</Trans>,
      key: 'Get Support',
      onClick: (event: React.MouseEvent) => {
        event.preventDefault();
        setFeedbackOpen(true);
      },
    },
    {
      href: '/',
      label: <Trans>Manage analytics</Trans>,
      key: 'Manage analytics',
      onClick: (event: React.MouseEvent) => {
        event.preventDefault();
        setAnalyticsConfigOpen(true);
      },
    },
  ];

  return (
    <Box
      sx={(theme) => ({
        width: '100%',
        boxShadow:
          theme.palette.mode === 'light'
            ? 'inset 0px 1px 0px rgba(0, 0, 0, 0.04)'
            : 'inset 0px 1px 0px rgba(255, 255, 255, 0.12)',
      })}
    >
      {/* Horizontal padding + maxWidth come from the themed MuiContainer breakpoint ladder, same as
          AppHeader, so the footer's content edges line up with the header's at every viewport width. */}
      <Container
        sx={{
          flexDirection: ['column', 'column', 'row'],
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '22px',
          pt: ['22px', '0px', '20px'],
          pb: ['40px', '0px', '20px'],
        }}
      >
        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {FOOTER_LINKS.map((link) => (
            <StyledLink onClick={link.onClick} key={link.key} href={link.href}>
              <Typography variant="caption">{link.label}</Typography>
            </StyledLink>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {FOOTER_ICONS.map((icon) => (
            <StyledLink href={icon.href} key={icon.title}>
              <SvgIcon
                sx={{
                  fontSize: [24, 24, 20],
                }}
              >
                {icon.icon}
              </SvgIcon>
            </StyledLink>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
